import { useCallback, useEffect, useRef } from 'react'
import { bridge } from '~/services/bridge'
import { getPluginAccessToken } from '~/services/bridge/pluginAuth'
import { shell } from '~/services/bridge/thinShellClient'

// Written into the popup the instant it opens, so the user sees a "Connecting…"
// state instead of a stark blank about:blank window during the async beforeOpen
// (preflight) step. Replaced when the popup navigates to the provider.
const OAUTH_LOADING_HTML = `<!doctype html><html><head><meta charset="utf-8"><title>Connecting…</title></head><body style="margin:0;height:100vh;display:flex;align-items:center;justify-content:center;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#555;background:#fff"><div style="text-align:center"><div style="width:28px;height:28px;margin:0 auto 12px;border:3px solid #e5e5e5;border-top-color:#888;border-radius:50%;animation:s .8s linear infinite"></div>Connecting…</div><style>@keyframes s{to{transform:rotate(360deg)}}</style></body></html>`

interface OAuthPopupOptions {
  width?: number
  height?: number
  /**
   * Async pre-work to run AFTER the popup window is opened but BEFORE it's
   * navigated to the OAuth URL (e.g. a plan/access preflight, or persisting
   * custom-connection creds). Must NOT be awaited by the caller before calling
   * openOAuthPopup — the window is opened synchronously inside the click's user
   * activation, then this runs. Resolve `false` (or throw) to abort: the popup
   * is closed and navigation is skipped.
   */
  beforeOpen?: () => Promise<boolean>
  onSuccess?: (data: { provider: string; connection_type: string }) => void
  onError?: (error: { provider: string; error: string }) => void
  onClose?: () => void
}

/**
 * Hook for handling OAuth flows in popup windows.
 * Both plugin and browser modes use the same approach: navigate to the login
 * endpoint with mode=redirect. The backend bounce page sets short-lived cookies
 * (oauth_t/oauth_w/oauth_o) then redirects to the provider. On callback the
 * cookies are present, so the backend resolves the workspace context and returns
 * an HTML page that sends postMessage back to the opener.
 */
export function useOAuthPopup() {
  const popupRef = useRef<Window | null>(null)
  const optionsRef = useRef<OAuthPopupOptions>({})

  const parseConnectionTypeFromApiUrl = (apiUrl: string): string | null => {
    const match = apiUrl.match(/\/oauth\/([^/]+)\/login/i)
    return match?.[1]?.toLowerCase() ?? null
  }

  /**
   * Wait for the callback server to receive the OAuth redirect.
   * The shell sends an `authCallback` bridge event with the URL query params
   * ({state, status}) when the one-shot localhost server handles the request.
   */
  const waitForAuthCallback = useCallback(
    (expectedState: string, timeoutMs = 300_000): Promise<boolean> => {
      return new Promise(resolve => {
        let settled = false

        const handler = (...args: unknown[]) => {
          const data = args[0] as Record<string, string> | undefined
          if (!data) return
          if (data.state === expectedState && data.status === 'connected') {
            if (!settled) {
              settled = true
              bridge.off('authCallback', handler)
              clearTimeout(timer)
            }
            resolve(true)
          }
        }

        const timer = setTimeout(() => {
          if (!settled) {
            settled = true
            bridge.off('authCallback', handler)
          }
          resolve(false)
        }, timeoutMs)

        bridge.on('authCallback', handler)
      })
    },
    []
  )

  // Message event listener
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // In production, validate event.origin for security
      if (event.data.type === 'oauth_success') {
        optionsRef.current.onSuccess?.(event.data)
        popupRef.current?.close()
        popupRef.current = null
      } else if (event.data.type === 'oauth_error') {
        optionsRef.current.onError?.(event.data)
        popupRef.current?.close()
        popupRef.current = null
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  // Popup close detection
  useEffect(() => {
    const checkPopupClosed = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        optionsRef.current.onClose?.()
        popupRef.current = null
        clearInterval(checkPopupClosed)
      }
    }, 500)

    return () => clearInterval(checkPopupClosed)
  }, [])

  const openOAuthPopup = useCallback(
    async (apiUrl: string, options: OAuthPopupOptions = {}) => {
      optionsRef.current = options

      try {
        // Plugin mode: open login URL directly in system browser with token + redirect mode.
        // This keeps the entire OAuth flow (login cookie → GitHub → callback cookie)
        // inside one browser session, so cookies are shared across all steps.
        if (bridge.isInPlugin()) {
          // Plugin mode opens the system browser (no window.open), so there's no
          // popup-blocking concern — just run the pre-work first and bail if it
          // denies/throws.
          if (options.beforeOpen) {
            try {
              if (!(await options.beforeOpen())) return
            } catch (e) {
              options.onError?.({
                provider: 'unknown',
                error: e instanceof Error ? e.message : 'Pre-connect step failed'
              })
              return
            }
          }

          const token = getPluginAccessToken()
          if (!token) {
            options.onError?.({ provider: 'unknown', error: 'Not authenticated' })
            return
          }

          // Start a callback server in the shell so the backend can
          // redirect the browser back after OAuth completes.
          let callbackUrl: string | undefined
          let callbackState: string | undefined
          try {
            const cb = await shell.startAuthCallbackServer()
            callbackUrl = cb.callbackUrl
            callbackState = cb.state
          } catch (err) {
            console.error('[useOAuthPopup] Failed to start auth callback server:', err)
          }

          const baseUrl = window.CURIETECH_SERVER_BASE_URL || bridge.getBaseUrl()
          const separator = apiUrl.includes('?') ? '&' : '?'
          let redirectUrl = `${baseUrl}${apiUrl}${separator}access_token=${encodeURIComponent(token)}&mode=redirect`
          if (callbackUrl) {
            // Base64url-encode so vscode.Uri.parse() can't mangle the nested URL
            const b64 = btoa(callbackUrl).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
            redirectUrl += `&callback_url=${b64}`
          }

          const openResult = await bridge.openInBrowser(redirectUrl)
          if (!openResult.success) {
            if (callbackUrl) shell.stopAuthCallbackServer().catch(() => {})
            options.onError?.({
              provider: 'unknown',
              error: openResult.error || 'Failed to open external browser'
            })
            return
          }

          const connectionType = parseConnectionTypeFromApiUrl(apiUrl)

          if (callbackState) {
            // Wait for the callback server to receive the browser redirect.
            // This is event-driven (not polling) so it works correctly even
            // when re-authorizing an already-connected provider.
            const completed = await waitForAuthCallback(callbackState)
            if (completed && connectionType) {
              options.onSuccess?.({
                provider: connectionType,
                connection_type: connectionType
              })
              return
            }
          }

          options.onClose?.()
          return
        }

        // Browser mode: same approach as plugin -- open popup to the login
        // endpoint with mode=redirect. The bounce page sets oauth_t/oauth_w/oauth_o
        // cookies, then redirects to the provider. On callback, the cookies are
        // present so workspace context is resolved and postMessage is sent back.
        // The access_token cookie (from the user's session) authenticates the request.
        const width = options.width || 600
        const height = options.height || 700
        const left = window.screen.width / 2 - width / 2
        const top = window.screen.height / 2 - height / 2

        const features = `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`

        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close()
        }

        // Open the window SYNCHRONOUSLY — this runs inside the click's user
        // activation (the caller must not await before calling openOAuthPopup).
        // Any async pre-work happens via options.beforeOpen AFTER the window
        // exists, so it can't consume the activation and get the popup blocked.
        const popup = window.open('about:blank', 'OAuth', features)
        popupRef.current = popup

        if (!popup) {
          console.error('Failed to open popup - popup blocked?')
          options.onError?.({
            provider: 'unknown',
            error: 'Failed to open popup window. Please allow popups for this site.'
          })
          return
        }

        // Paint a "Connecting…" placeholder immediately so the popup isn't a
        // blank window while beforeOpen runs (the preflight can take a beat,
        // especially right after disconnecting). Best-effort; navigation below
        // replaces it regardless.
        try {
          popup.document?.write?.(OAUTH_LOADING_HTML)
          popup.document?.close?.()
        } catch {
          // about:blank document quirks — non-fatal.
        }

        if (options.beforeOpen) {
          let allowed = false
          try {
            allowed = await options.beforeOpen()
          } catch (e) {
            popup.close()
            popupRef.current = null
            options.onError?.({
              provider: 'unknown',
              error: e instanceof Error ? e.message : 'Pre-connect step failed'
            })
            return
          }
          if (!allowed) {
            // Denied (e.g. plan gate). The preflight already surfaced the reason
            // (upgrade modal / toast); just close the blank popup.
            popup.close()
            popupRef.current = null
            return
          }
        }

        // The user may have closed the "Connecting…" popup during beforeOpen.
        // Navigating a closed window is a no-op at best and can throw — so just
        // treat it as a cancelled flow.
        if (popup.closed) {
          popupRef.current = null
          options.onClose?.()
          return
        }

        const separator = apiUrl.includes('?') ? '&' : '?'
        popup.location.href = `${apiUrl}${separator}mode=redirect`
      } catch (error) {
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close()
          popupRef.current = null
        }
        console.error('OAuth popup error:', error)
        options.onError?.({
          provider: 'unknown',
          error: error instanceof Error ? error.message : 'Unknown error occurred'
        })
      }
    },
    [waitForAuthCallback]
  )

  return { openOAuthPopup }
}
